import { useEffect, useRef, useState, useCallback } from 'react';

interface VersionInfo {
  version: string;
  buildTime: string;
}

interface UseVersionCheckerOptions {
  checkInterval?: number; // in milliseconds, default 60 seconds
  onNewVersionAvailable?: (newVersion: string) => void;
  autoReload?: boolean;
}

const VERSION_KEY = 'app_version';
const CHECK_INTERVAL_DEFAULT = 60 * 1000; // 60 seconds

export const useVersionChecker = (options: UseVersionCheckerOptions = {}) => {
  const {
    checkInterval = CHECK_INTERVAL_DEFAULT,
    onNewVersionAvailable,
    autoReload = false,
  } = options;

  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const initialVersionRef = useRef<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchVersion = useCallback(async (): Promise<VersionInfo | null> => {
    try {
      // Add timestamp to bypass any caching
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('Version check failed:', response.status);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.warn('Version check error:', error);
      return null;
    }
  }, []);

  const checkVersion = useCallback(async () => {
    const versionInfo = await fetchVersion();

    if (!versionInfo?.version) return;

    // Store initial version on first check
    if (!initialVersionRef.current) {
      initialVersionRef.current = versionInfo.version;
      setCurrentVersion(versionInfo.version);

      // Also store in localStorage for persistence across tabs
      localStorage.setItem(VERSION_KEY, versionInfo.version);
      return;
    }

    // Compare with initial version
    if (versionInfo.version !== initialVersionRef.current) {
      console.log('New version detected:', versionInfo.version, 'Current:', initialVersionRef.current);
      setIsNewVersionAvailable(true);
      setCurrentVersion(versionInfo.version);

      if (onNewVersionAvailable) {
        onNewVersionAvailable(versionInfo.version);
      }

      if (autoReload) {
        // Clear cache and reload
        if ('caches' in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name));
          });
        }
        window.location.reload();
      }
    }
  }, [fetchVersion, onNewVersionAvailable, autoReload]);

  const reloadToNewVersion = useCallback(() => {
    // Clear any cached data
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }

    // Update stored version before reload
    if (currentVersion) {
      localStorage.setItem(VERSION_KEY, currentVersion);
    }

    // Force reload from server
    window.location.reload();
  }, [currentVersion]);

  const dismissUpdate = useCallback(() => {
    setIsNewVersionAvailable(false);
    // Update the initial version ref so we don't keep showing the notification
    if (currentVersion) {
      initialVersionRef.current = currentVersion;
    }
  }, [currentVersion]);

  useEffect(() => {
    // Initial version check
    checkVersion();

    // Set up periodic checking
    intervalRef.current = setInterval(checkVersion, checkInterval);

    // Also check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check when window regains focus
    const handleFocus = () => {
      checkVersion();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion, checkInterval]);

  return {
    isNewVersionAvailable,
    currentVersion,
    reloadToNewVersion,
    dismissUpdate,
    checkVersion, // Manual trigger
  };
};

export default useVersionChecker;
